import React, { useMemo } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import { Box, Typography } from '@mui/material';
import { CUSTOM_SERVICE_LABEL } from '../../../utils/serviceTypeDisplay';
import { getWizardUrgencyLabel } from '../../../constants/wizardUrgencyOptions';
import WizardPhotoGallery from '../WizardPhotoGallery/WizardPhotoGallery';
import {
  StyledRequestSummaryDialog,
  RequestSummaryDialogTitle,
  RequestSummaryDialogCloseButton,
  RequestSummaryDialogContent,
  RequestSummaryDialogActions,
  RequestSummaryCloseButton,
  RequestSummaryReviewList,
  RequestSummaryReviewItem,
  RequestSummaryReviewItemPhotos,
  RequestSummaryReviewLabel,
  RequestSummaryReviewValue,
  RequestSummaryReviewValuePhotos,
} from '../WizardRequestSummaryDialog/WizardRequestSummaryDialog.styled';
import { ReadOnlyNotice } from './CustomServiceRequestDetailDialog.styled';

function formatSentAt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return '—';
  }
}

/**
 * Read-only detail for a custom storefront inquiry (no edit / no reuse actions).
 */
export default function CustomServiceRequestDetailDialog({
  open,
  onClose,
  requestRow,
  businessNameById = {},
}) {
  const presentation = useMemo(() => {
    if (!requestRow?.formData) return null;

    const rows = [
      {
        label: 'Service type',
        value: CUSTOM_SERVICE_LABEL,
      },
    ];

    const targetTitle = requestRow.targetServiceTitle;
    if (targetTitle) {
      rows.push({ label: 'Service', value: targetTitle });
    }

    const urgencyDisplay =
      requestRow.requestSummary?.urgency ||
      getWizardUrgencyLabel(requestRow.formData.urgency);
    if (urgencyDisplay && urgencyDisplay !== '—') {
      rows.push({ label: 'Urgency', value: urgencyDisplay });
    }

    const locationDisplay =
      requestRow.requestSummary?.location ||
      (requestRow.formData.city?.trim()
        ? [requestRow.formData.city.trim(), requestRow.formData.state?.trim() || 'KY']
            .filter(Boolean)
            .join(', ')
        : '');
    if (locationDisplay) {
      rows.push({ label: 'Location', value: locationDisplay });
    }

    if (requestRow.formData.details?.trim()) {
      rows.push({ label: 'What you need', value: requestRow.formData.details.trim() });
    }

    const photoItems = (requestRow.formData.photos || [])
      .filter(Boolean)
      .map((url, i) => ({ id: `photo-${i}`, url }));
    if (photoItems.length > 0) {
      rows.push({ label: 'Photos', value: `${photoItems.length} photo(s)` });
    }

    const sentLeads = requestRow.leads || [];
    if (sentLeads.length > 0) {
      const primary = sentLeads[0];
      const businessName =
        businessNameById[primary.businessId] || 'Business';
      rows.push({ label: 'Sent to', value: businessName });
    } else {
      rows.push({
        label: 'Sent to',
        value: '—',
      });
    }

    const submittedAt =
      requestRow.createdAt || sentLeads[0]?.sentAt;
    if (submittedAt) {
      rows.push({ label: 'Submitted', value: formatSentAt(submittedAt) });
    }

    return {
      title: 'Custom service inquiry',
      subtitle: targetTitle || CUSTOM_SERVICE_LABEL,
      rows,
      photoItems,
    };
  }, [requestRow, businessNameById]);

  const titleText = presentation?.title ?? 'Custom service inquiry';
  const subtitleText = (presentation?.subtitle || '').trim();

  return (
    <StyledRequestSummaryDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      aria-labelledby="custom-service-request-detail-title"
    >
      <RequestSummaryDialogTitle id="custom-service-request-detail-title">
        <span className="request-summary-dialog-icon-wrap" aria-hidden>
          <StorefrontRoundedIcon className="request-summary-dialog-icon" />
        </span>
        <Box className="request-summary-dialog-heading">
          <span className="request-summary-dialog-main-title">{titleText}</span>
          {subtitleText ? (
            <span className="request-summary-dialog-subtitle">{subtitleText}</span>
          ) : null}
        </Box>
        <RequestSummaryDialogCloseButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </RequestSummaryDialogCloseButton>
      </RequestSummaryDialogTitle>

      <RequestSummaryDialogContent>
        {presentation ? (
          <>
            <RequestSummaryReviewList>
              {presentation.rows.map((row, i) => {
                const photoItems = presentation.photoItems ?? [];
                const isPhotosRow = row.label === 'Photos' && photoItems.length > 0;
                const Item = isPhotosRow
                  ? RequestSummaryReviewItemPhotos
                  : RequestSummaryReviewItem;
                return (
                  <Item key={`${row.label}-${i}`}>
                    <RequestSummaryReviewLabel>{row.label}</RequestSummaryReviewLabel>
                    {isPhotosRow ? (
                      <RequestSummaryReviewValuePhotos>
                        <WizardPhotoGallery items={photoItems} inline />
                      </RequestSummaryReviewValuePhotos>
                    ) : (
                      <RequestSummaryReviewValue>{row.value}</RequestSummaryReviewValue>
                    )}
                  </Item>
                );
              })}
            </RequestSummaryReviewList>
            <ReadOnlyNotice>
              This inquiry was sent to one business. You cannot reuse it for other providers.
            </ReadOnlyNotice>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Details for this request are unavailable.
          </Typography>
        )}
      </RequestSummaryDialogContent>

      <RequestSummaryDialogActions>
        <RequestSummaryCloseButton variant="outlined" color="inherit" onClick={onClose}>
          Close
        </RequestSummaryCloseButton>
      </RequestSummaryDialogActions>
    </StyledRequestSummaryDialog>
  );
}
